import { Button, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from 'reactstrap';

import {
  ContinuityEventStatus,
  ContinuityStatusResponse,
} from '../../../types/tuitionContinuityV2';
import { formatDate } from '../../../utils/dates';

const prefix = 'tuitionContinuityV2.eventStatus';

interface EventStatusMessageProps {
  status: ContinuityStatusResponse;
  onGoToTuition: () => void;
  onDownloadVoucher: () => void;
}

export default function EventStatusMessage({
  status,
  onGoToTuition,
  onDownloadVoucher,
}: EventStatusMessageProps) {
  const { t } = useTranslation();

  const content = useMemo(() => {
    const fmt = (date: string | null) =>
      date ? formatDate(date, 'DD-MM-YYYY') : '';

    switch (status.eventStatus) {
      case ContinuityEventStatus.ACTIVE:
        return {
          icon: 'success' as const,
          title: t(`${prefix}.active.title`),
          body: t(`${prefix}.active.body`, {
            start: fmt(status.startDate),
            end: fmt(status.endDate),
          }),
          showButtons: true,
        };
      case ContinuityEventStatus.FUTURE:
        return {
          icon: 'information' as const,
          title: t(`${prefix}.future.title`),
          body: t(`${prefix}.future.body`, {
            start: fmt(status.startDate),
            end: fmt(status.endDate),
          }),
          showButtons: false,
        };
      case ContinuityEventStatus.PAST:
        return {
          icon: 'information' as const,
          title: t(`${prefix}.past.title`),
          body: t(`${prefix}.past.body`, {
            start: fmt(status.startDate),
            end: fmt(status.endDate),
          }),
          showButtons: false,
        };
      case ContinuityEventStatus.NO_LIQUIDATION:
        return {
          icon: 'warning' as const,
          title: t(`${prefix}.noLiquidation.title`),
          body: t(`${prefix}.noLiquidation.body`),
          showButtons: false,
        };
      case ContinuityEventStatus.NO_EVENT:
      default:
        return {
          icon: 'information' as const,
          title: t(`${prefix}.noEvent.title`),
          body: t(`${prefix}.noEvent.body`),
          showButtons: false,
        };
    }
  }, [status, t]);

  return (
    <Card
      className="d-flex justify-content-center align-items-center mt-3 w-75 mx-auto p-3"
      style={{ background: '#f7f7f7', minHeight: 333 }}
    >
      <Icon
        className="mx-auto"
        name={content.icon}
        size="65px"
        color="secondary"
      />
      <div className="text-center p-3">
        <p className="fs-20 text-dark">{content.title}</p>
        <p className="fs-18 fw-300">{content.body}</p>
        {content.showButtons && (
          <div
            className="d-flex flex-column align-items-center gap-3 mt-3"
            style={{
              gap: 8,
            }}
          >
            <Button
              text={t(`${prefix}.buttonGoToTuition`)}
              size="lg"
              style={{ minWidth: 320 }}
              onClick={onGoToTuition}
            />
            <Button
              text={t(`${prefix}.buttonDownloadVoucher`)}
              size="lg"
              outlined
              style={{ minWidth: 320 }}
              onClick={onDownloadVoucher}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
